// Shared helpers for event views.

const STATUS_COLOR = {
  draft: 'bg-muted text-muted-foreground',
  sized_up: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
  pending_announce: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  announced: 'bg-green-500/10 text-green-700 dark:text-green-300',
  cancelled: 'bg-destructive/10 text-destructive',
};

const STATUS_DOT = {
  draft: 'bg-muted-foreground',
  sized_up: 'bg-blue-500',
  pending_announce: 'bg-amber-500',
  announced: 'bg-green-500',
  cancelled: 'bg-destructive',
};

// Labels deliberately match LIFECYCLE_LABEL (Ready / Queued / Live …) so the
// kanban columns read the same words as the list/calendar badges — one
// vocabulary everywhere. (DB enum values are unchanged.)
export const STATUS_BUCKETS = [
  { key: 'sized_up',         label: 'Ready' },
  { key: 'pending_announce', label: 'Queued' },
  { key: 'announced',        label: 'Live' },
  { key: 'cancelled',        label: 'Cancelled' },
];

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLOR[status] || ''}`}>
      {(status || 'draft').replace('_', ' ')}
    </span>
  );
}

// `past` de-saturates the dot to a neutral grey so past nights recede and the
// status colors of upcoming events stand out.
export function StatusDot({ status, className = '', past = false }) {
  const color = past ? 'bg-muted-foreground/40' : (STATUS_DOT[status] || 'bg-muted');
  return <span className={`inline-block w-2 h-2 rounded-full ${color} ${className}`} />;
}

const PUB_COLOR = {
  published: 'bg-green-500',
  manual_only: 'bg-amber-400',
  pending: 'bg-muted-foreground',
  failed: 'bg-red-500',
  unpublished: 'bg-muted',
};

export function PublicationPip({ pub }) {
  return (
    <span
      title={`${pub.platform}: ${pub.status}`}
      aria-label={`${pub.platform}: ${pub.status}`}
      role="img"
      className={`inline-block w-2 h-2 rounded-full mr-1 ${PUB_COLOR[pub.status] || 'bg-muted'}`}
    />
  );
}

// Single-pill rollup that collapses the per-platform dots into one
// glanceable status. Precedence: Failed > Pending > Ready. Hover tooltip
// lists each platform:status in plain text.
export function PublicationRollup({ publications }) {
  if (!publications?.length) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const failed  = publications.filter((p) => p.status === 'failed');
  const pending = publications.filter((p) => p.status === 'pending');
  const tooltip = publications.map((p) => `${p.platform}: ${p.status}`).join(' · ');

  let tone, label;
  if (failed.length) {
    tone  = 'bg-red-500/15 text-red-700 dark:text-red-300';
    label = `✗ Failed · ${failed.length}`;
  } else if (pending.length) {
    tone  = 'bg-amber-400/15 text-amber-700 dark:text-amber-400';
    label = `⏳ Pending · ${pending.length}`;
  } else {
    tone  = 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    label = `✓ Ready`;
  }

  return (
    <span title={tooltip} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tone}`}>
      {label}
    </span>
  );
}

export function formatDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatDateLong(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// Parse a Postgres date (ISO with T00:00:00Z) into a {year, month, day} (UTC).
export function asUtcParts(iso) {
  const d = new Date(iso);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() };
}

// Group events by their (UTC) date string YYYY-MM-DD.
export function groupByDate(events) {
  const byDate = new Map();
  for (const e of events) {
    const { y, m, d } = asUtcParts(e.date);
    const key = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key).push(e);
  }
  return byDate;
}

// useEvents() lives in ./useEvents.js — kept separate to keep this file's
// React surface minimal (badges + helpers only).

// ── Canonical lifecycle labels / badges — single source of truth ───────────
// EventsList/Calendar/Kanban all derive `lifecycle_status` server-side
// (past / cancelled / announced / pending_announce / sized_up / draft).
// The label maps below are imported everywhere — never inline these.

export const LIFECYCLE_LABEL = {
  draft:            'Draft',
  sized_up:         'Ready',
  pending_announce: 'Queued',
  announced:        'Live',
  cancelled:        'Cancelled',
  past:             'Past',
};

export const LIFECYCLE_BADGE = {
  draft:            'bg-muted text-muted-foreground',
  sized_up:         'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  pending_announce: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400',
  announced:        'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  cancelled:        'bg-destructive/15 text-destructive',
  past:             'bg-muted text-muted-foreground italic',
};

export function LifecycleBadge({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${LIFECYCLE_BADGE[status] || ''}`}>
      {LIFECYCLE_LABEL[status] || status}
    </span>
  );
}

// ── Weekday → residency template name (used by "+ Create placeholder") ────
// Calendar empty-cell affordance auto-picks the right residency template
// by the cell's weekday. Special Event Template covers special / one-off
// dates that don't slot into a residency.

export const WEEKDAY_TEMPLATE_MAP = {
  0: 'LIV ON SUNDAY',        // Sun
  1: null,                   // Mon (no residency)
  2: null,                   // Tue
  3: null,                   // Wed
  4: 'Mentirosa Thursday',   // Thu
  5: 'Friday Night',         // Fri
  6: 'Saturday Night',       // Sat
};

// ── DriftDot — single SVG dot, color by has_drift state ─────────────────
// Reads event.has_drift (BOOL_OR aggregate from event_platform_sync set
// by the drift cron + on-view checks). Inline tooltip surfaces last-check
// timestamp so ops can tell if a green dot is fresh or stale.

// Glyph (not color alone) per state so the signal survives color-blindness /
// greyscale: ✓ in sync · ◆ drifted · ○ not published. Suppressed entirely on
// cancelled/past events, where drift is irrelevant noise.
export function DriftDot({ event, size = 8 }) {
  const status = event.lifecycle_status || event.status;
  if (status === 'cancelled' || status === 'past') return null;

  const fontSize = size + 3;
  const glyphStyle = { fontSize, lineHeight: 1 };
  const hasPub = (event.publications || []).some((p) => p.platform === 'universe' && p.external_url);
  if (!hasPub) {
    return (
      <span role="img" aria-label="Not published to Universe" title="No Universe publication"
        className="inline-block text-muted-foreground align-middle" style={glyphStyle}>○</span>
    );
  }
  const drift = event.has_drift === true;
  const lastCheck = event.last_drift_check_at
    ? new Date(event.last_drift_check_at).toLocaleString()
    : 'never';
  const title = drift
    ? `Drifted from Universe · last check ${lastCheck}`
    : `In sync with Universe · last check ${lastCheck}`;
  return (
    <span role="img" aria-label={title} title={title}
      className={`inline-block align-middle ${drift ? 'text-amber-500' : 'text-emerald-500'}`}
      style={glyphStyle}>{drift ? '◆' : '✓'}</span>
  );
}

// Small inline key for the drift glyphs — drop it where the dots cluster
// (list/calendar header) so the symbols are self-explanatory.
export function DriftLegend({ className = '' }) {
  return (
    <div className={`flex items-center gap-3 text-[10px] text-muted-foreground ${className}`}>
      <span className="text-emerald-500">✓ <span className="text-muted-foreground">in sync</span></span>
      <span className="text-amber-500">◆ <span className="text-muted-foreground">drifted</span></span>
      <span>○ not published</span>
    </div>
  );
}

// ── RowActions — contextual primary action for an event row ───────────────
// Renders ONE button reflecting the row's current state. Click fires the
// matching endpoint; uses navigate() to deep-link for the "Check drift" /
// "Open" cases. Callers pass `onActionComplete` to refetch the list after
// a state-changing action.

import { useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useNavigate } from 'react-router-dom';

export function RowActions({ event, tenantSlug, onActionComplete, onBook }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const hasFlyer = !!event.flyer_image_url;
  const hasTalent = !!event.talent && !/^(tbd|tba|placeholder)$/i.test(String(event.talent).trim());
  const drift = event.has_drift === true;
  const status = event.lifecycle_status;

  const post = async (path) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${event.id}/${path}?authorize_writes=1`, {
        method: 'POST',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'action_failed');
      if (onActionComplete) onActionComplete();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Stop click from also navigating to event detail.
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const goEdit = () => navigate(`/${tenantSlug}/event/${event.id}`);
  const doClone = async () => {
    const date = window.prompt('Duplicate this event to which date? (YYYY-MM-DD)', '');
    if (!date) return;
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${event.id}/clone`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'clone_failed');
      if (onActionComplete) onActionComplete();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const doCancel = async () => {
    if (!window.confirm('Cancel this event? It will be marked cancelled.')) return;
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${event.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'cancelled' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'cancel_failed');
      if (onActionComplete) onActionComplete();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // Picks one action per row state, in priority order:
  //   drift > announce-ready > sync-needed > open
  if (status === 'cancelled' || status === 'past') return null;

  let action = null;
  if (drift) {
    action = (
      <button
        onClick={(e) => { stop(e); navigate(`/${tenantSlug}/event/${event.id}`); }}
        className="text-[10px] px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-500/20 whitespace-nowrap"
        title="Drift detected, open event to compare and resolve"
      >Check drift</button>
    );
  } else if ((status === 'sized_up' || status === 'pending_announce') && hasTalent) {
    action = (
      <button
        onClick={(e) => { stop(e); post('announce'); }}
        disabled={busy} aria-disabled={busy}
        className="text-[10px] px-1.5 py-0.5 border border-primary bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        title={hasFlyer ? 'Announce: set Live + fan out to all platforms' : 'Will use template default flyer'}
      >{busy ? 'Announcing…' : 'Announce + sync'}</button>
    );
  } else if (!hasTalent && (status === 'draft' || status === 'sized_up' || status === 'pending_announce')) {
    // Unbooked placeholder → the natural next step is to book talent.
    action = (
      <button
        onClick={(e) => { stop(e); onBook ? onBook(event) : goEdit(); }}
        className="text-[10px] px-1.5 py-0.5 border border-primary bg-primary text-primary-foreground rounded hover:opacity-90 whitespace-nowrap"
        title="Book talent for this date"
      >+ Book</button>
    );
  } else if (status === 'draft' || status === 'sized_up' || status === 'pending_announce') {
    action = (
      <button
        onClick={(e) => { stop(e); post('sync-platforms'); }}
        disabled={busy} aria-disabled={busy}
        className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50 whitespace-nowrap"
        title="Push placeholder structure to all platforms"
      >{busy ? 'Pushing…' : '+ Push placeholder'}</button>
    );
  } else if (status === 'announced') {
    action = (
      <button
        onClick={(e) => { stop(e); post('sync-platforms'); }}
        disabled={busy} aria-disabled={busy}
        className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50 whitespace-nowrap"
        title="Sync canonical state to all platforms (no status change)"
      >{busy ? 'Syncing…' : 'Sync'}</button>
    );
  }

  // Always surface the error visibly (the old code rendered a lone "!").
  return (
    <span className="inline-flex flex-col items-end gap-0.5" onClick={stop}>
      <span className="inline-flex items-center gap-1">
        {action}
        <div className="relative">
          <button onClick={(e) => { stop(e); setMenuOpen((o) => !o); }} aria-label="More actions" title="More actions"
            className="px-1 text-muted-foreground hover:text-foreground leading-none">⋯</button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={(e) => { stop(e); setMenuOpen(false); }} />
              <div className="absolute right-0 z-50 mt-1 w-40 rounded-md border border-border bg-card shadow-lg py-1 text-xs text-left">
                <button onClick={(e) => { stop(e); setMenuOpen(false); goEdit(); }} className="block w-full text-left px-3 py-1.5 hover:bg-accent">Edit</button>
                {onBook && !hasTalent && (
                  <button onClick={(e) => { stop(e); setMenuOpen(false); onBook(event); }} className="block w-full text-left px-3 py-1.5 hover:bg-accent">Book talent…</button>
                )}
                {status !== 'announced' && (
                  <button onClick={(e) => { stop(e); setMenuOpen(false); post('sync-platforms'); }} className="block w-full text-left px-3 py-1.5 hover:bg-accent">Push placeholder</button>
                )}
                <button onClick={(e) => { stop(e); setMenuOpen(false); doClone(); }} className="block w-full text-left px-3 py-1.5 hover:bg-accent">Duplicate…</button>
                <button onClick={(e) => { stop(e); setMenuOpen(false); doCancel(); }} className="block w-full text-left px-3 py-1.5 hover:bg-accent text-destructive">Cancel event</button>
              </div>
            </>
          )}
        </div>
      </span>
      {err && <span className="text-[10px] text-destructive max-w-[140px] truncate" title={err}>⚠ {err}</span>}
    </span>
  );
}
